import { Box, Image, Skeleton, Text, type MantineRadius } from "@mantine/core";
import { useSignedImageUrl } from "../../hooks/useSignedImageUrl";

// Renders a private-bucket S3 image by resolving a short-lived presigned GET URL.
// Presigned URLs are cached/deduped by react-query (valid ~1h on the backend).
export function S3Image({
  src,
  alt,
  h = 120,
  w,
  fit = "cover",
  radius = "md",
  emptyLabel,
}: {
  src?: string | null;
  alt?: string;
  h?: number | string;
  w?: number | string;
  fit?: "cover" | "contain";
  radius?: MantineRadius;
  emptyLabel?: string;
}) {
  const { key, url, isLoading, isError } = useSignedImageUrl(src);

  if (!key) {
    return <Placeholder h={h} w={w} radius={radius} label={emptyLabel ?? "No image"} />;
  }
  if (isLoading) {
    return <Skeleton h={h} w={w ?? "100%"} radius={radius} />;
  }
  if (isError || !url) {
    return <Placeholder h={h} w={w} radius={radius} label="Unavailable" />;
  }
  return (
    <Image
      src={url}
      h={h}
      w={w}
      radius={radius}
      fit={fit}
      alt={alt ?? "image"}
    />
  );
}

function Placeholder({
  h,
  w,
  radius,
  label,
}: {
  h: number | string;
  w?: number | string;
  radius: MantineRadius;
  label: string;
}) {
  return (
    <Box
      h={h}
      w={w ?? "100%"}
      style={{
        border: "1px dashed var(--mantine-color-gray-4)",
        borderRadius: "var(--mantine-radius-md)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      data-radius={radius}
    >
      <Text c="dimmed" size="xs">
        {label}
      </Text>
    </Box>
  );
}
